import { cs } from '../../../lib/currency';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Ban, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * iter334ag — Void a monthly_invoice safely.
 *
 * Two-phase flow so admins never accidentally void an MI whose
 * linked invoice/PI is still live:
 *   1. Dry-run to surface any blockers (linked invoice not yet
 *      void/refunded, cash captured with no PI to validate against,
 *      etc.).
 *   2. If clean OR admin explicitly ticks "Force", call apply.
 *
 * The invoice-side void / refund is a separate flow (Parent → Billing
 * → Invoice actions). This dialog is strictly for the MI-side void.
 */
export default function VoidMiDialog({ open, onOpenChange, invoice, onVoided }) {
  const [reason, setReason] = useState('');
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [validation, setValidation] = useState(null); // { blockers, current_state } from dry-run

  if (!invoice) return null;

  const miId = invoice.monthly_invoice_id || invoice.id;

  const reset = () => {
    setReason('');
    setForce(false);
    setValidation(null);
  };

  const handleClose = (v) => {
    if (busy) return;
    if (!v) reset();
    onOpenChange(v);
  };

  const runValidation = async () => {
    if (!reason.trim()) {
      toast.error('Please enter a reason for voiding this MI.');
      return;
    }
    setBusy(true);
    setValidation(null);
    try {
      const res = await fetch('/api/admin/billing/mi-void', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          mi_id: miId,
          reason: reason.trim(),
          dry_run: true,
          force: false,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body?.detail || 'Validation failed');
        return;
      }
      setValidation(body);
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const runApply = async () => {
    if (!reason.trim()) {
      toast.error('Please enter a reason for voiding this MI.');
      return;
    }
    const hasBlockers = validation && (validation.blockers || []).length > 0;
    if (hasBlockers && !force) {
      toast.error('Blockers detected — tick "Force" to override, or void/refund the linked invoice first.');
      return;
    }
    if (!window.confirm(`Void MI ${miId.slice(0, 8)}…? This cannot be undone from the UI.`)) return;
    setBusy(true);
    try {
      const res = await fetch('/api/admin/billing/mi-void', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          mi_id: miId,
          reason: reason.trim(),
          dry_run: false,
          force,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        toast.error(body?.detail || 'Void failed');
        return;
      }
      if (body.skipped_reason === 'already_void') {
        toast.info('MI is already void — no changes made.');
      } else if (body.voided) {
        toast.success(`MI ${miId.slice(0, 8)}… voided.`);
        onVoided && onVoided(body);
      } else if ((body.blockers || []).length) {
        toast.error(`Void refused: ${body.blockers.join(' · ')}`);
        setValidation(body);
        setBusy(false);
        return;
      }
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const blockers = (validation && validation.blockers) || [];
  const curState = (validation && validation.current_state) || null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg" data-testid="void-mi-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-rose-600" />
            Void Monthly Invoice
          </DialogTitle>
          <DialogDescription>
            Voids the MI record only. If the linked invoice / Stripe charge
            still has captured cash, void or fully refund it from the
            Parent → Billing section BEFORE voiding the MI here.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-0.5" data-testid="void-mi-context">
            <div><span className="text-slate-500">MI ID:</span> <span className="font-mono">{miId}</span></div>
            {invoice.billing_period && (
              <div><span className="text-slate-500">Billing Month:</span> {invoice.billing_period}</div>
            )}
            <div>
              <span className="text-slate-500">Amount / Charged / Refunded:</span>{' '}
              {cs()}{Number(invoice.amount || 0).toFixed(2)} · {cs()}
              {Number(invoice.amount_charged || 0).toFixed(2)} · {cs()}
              {Number(invoice.refunded_amount || 0).toFixed(2)}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500">Reason (required)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="e.g. Duplicate of MI 3b5ce030… — linked invoice was voided from Parent Billing"
              data-testid="void-mi-reason"
              disabled={busy}
            />
          </div>

          {validation && (
            <div
              className={`rounded-md border px-3 py-2 text-xs space-y-1 ${
                blockers.length === 0
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                  : 'border-amber-300 bg-amber-50 text-amber-900'
              }`}
              data-testid="void-mi-validation"
            >
              {blockers.length === 0 ? (
                <div className="font-medium">
                  ✓ Preflight passed — this MI is safe to void.
                </div>
              ) : (
                <>
                  <div className="font-medium flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" />
                    {blockers.length} blocker{blockers.length === 1 ? '' : 's'} detected
                  </div>
                  <ul className="list-disc pl-4 space-y-0.5">
                    {blockers.map((b, i) => (
                      <li key={i} data-testid={`void-mi-blocker-${i}`}>{b}</li>
                    ))}
                  </ul>
                </>
              )}
              {curState && (
                <div className="pt-1 border-t border-current/10 text-[11px]">
                  Current: status=<code>{curState.status}</code>{' '}
                  charged={cs()}{Number(curState.amount_charged || 0).toFixed(2)}{' '}
                  refunded={cs()}{Number(curState.refunded_amount || 0).toFixed(2)}
                  {curState.stripe_payment_intent_id && <> · PI=<code>{curState.stripe_payment_intent_id.slice(0, 14)}…</code></>}
                </div>
              )}
            </div>
          )}

          {validation && blockers.length > 0 && (
            <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={force}
                onChange={(e) => setForce(e.target.checked)}
                data-testid="void-mi-force"
                disabled={busy}
              />
              <span>
                <strong>Force void</strong> — bypass the blockers above. The
                blockers will be recorded in the audit trail. Use only when
                you&apos;ve reconciled the linked charge manually.
              </span>
            </label>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={busy} data-testid="void-mi-cancel">
            Cancel
          </Button>
          {!validation && (
            <Button
              onClick={runValidation}
              disabled={busy || !reason.trim()}
              data-testid="void-mi-preflight"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Preflight Check
            </Button>
          )}
          {validation && (
            <Button
              variant="destructive"
              onClick={runApply}
              disabled={busy || (blockers.length > 0 && !force)}
              data-testid="void-mi-apply"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Ban className="h-4 w-4 mr-1" />}
              Void MI
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
